import cx from 'clsx';
import { format, toZonedTime } from 'date-fns-tz';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as ChartArrowIcon } from '@assets/svg/ic-chevron.svg';
import { ReactComponent as RefetchIcon } from '@assets/svg/ic-return.svg';
import { ReactComponent as ThreeDotsMoreIcon } from '@assets/svg/three-dots.svg';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import DatePicker from '@components/form/DatePicker/DatePicker';
import Tabs from '@components/uikit/Tabs/Tabs';

import { getDefaultDateTime } from '@helpers/getDefaultDateTime';

import useTranslation from '@hooks/useTranslation';

import type { IChartType } from '@model/chartType';
import type { IInstallationPoint } from '@model/installationPoint';

import { getChartPrintTitle } from '@pages/SensorDetails/helpers/getChartPrintTitle';

import { useChartFftQuery } from '@services/useChartFftQuery';

import { selectAppPersistDarkMode } from '@store/useAppStorePersist';

import { FFT_TWF_CHARTS_KEYS } from '../../constants/fftTwfChartsKeys';
import { getAvailableTimes } from '../../helpers/getAvailableTimes';
import { getChartTwfFftOptions } from '../../helpers/getChartTwfFftOptions';
import { useFftTimestampsImpactQuery } from '../../services/useFftTimestampsImpactQuery';
import { useFftTimestampsQuery } from '../../services/useFftTimestampsQuery';
import DownloadContextMenu from '../DownloadContextMenu/DownloadContextMenu';
import style from './style.module.scss';

interface IProps {
  point: IInstallationPoint;
  chartType: IChartType;
  dateTimeFormat: string;
}

const fftMetricsList = [
  { value: 'hz', label: 'Hz' },
  { value: 'cpm', label: 'CPM' },
];

const ChartTwfFftView: React.FC<IProps> = ({ point, chartType, dateTimeFormat }) => {
  const { t } = useTranslation();

  const [isOpenChart, setIsOpenChart] = useState(true);
  const [axisId, setAxisId] = useState(chartType?.series?.[0]?.axisId);
  const [currentMetric, setCurrentMetric] = useState('hz');
  const [selectedDateTime, setSelectedDateTime] = useState({ date: '', time: '' });

  const printRef = useRef<HTMLDivElement | null>(null);
  const chartComponentRef = useRef(null);

  const darkMode = selectAppPersistDarkMode();

  const { isFetching: isTimestampsFetching, data: timestampsData } = useFftTimestampsQuery({
    enabled: !!point?.id && +axisId !== 4,
    pointId: point?.id,
    axisId,
  });

  const { isFetching: isTimestampsImpactFetching, data: timestampsImpactData } = useFftTimestampsImpactQuery({
    pointId: point?.id,
    axisId: 1,
  });

  const currentTimestamps = +axisId === 4 ? timestampsImpactData : timestampsData;

  useEffect(() => {
    if (currentTimestamps) {
      if (!currentTimestamps?.[selectedDateTime.date || '']?.[selectedDateTime.time || '']?.id) {
        const defaultDateTime = getDefaultDateTime(currentTimestamps);
        setSelectedDateTime({ date: defaultDateTime.date, time: defaultDateTime.time });
      }
    }
  }, [currentTimestamps]);

  const readingId = currentTimestamps?.[selectedDateTime.date || '']?.[selectedDateTime.time || '']?.id;

  const {
    isFetching: isChartFetching,
    data: fftQuery,
    refetch,
  } = useChartFftQuery({
    enabled: !!currentTimestamps && !!readingId,
    readingId,
    isImpactVue: +axisId === 4,
  });

  const chartKey = FFT_TWF_CHARTS_KEYS[chartType.alias as keyof typeof FFT_TWF_CHARTS_KEYS]!;

  const currentChartType = fftQuery?.[chartKey]?.type;
  const currentChartData = fftQuery?.[chartKey]?.data || [];
  const isFftChart = chartType.type === 'fft';
  const availableTimes = getAvailableTimes({ timestamps: currentTimestamps, date: selectedDateTime.date });
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcDate =
    selectedDateTime.date && selectedDateTime.time
      ? new Date(`${selectedDateTime.date}T${selectedDateTime.time}:00Z`)
      : null;

  const currentDate = toZonedTime(utcDate || new Date(), userTimezone);
  const currentTimestamp = currentDate ? format(currentDate || '', dateTimeFormat) : '';
  const chartOptions = getChartTwfFftOptions(
    t,
    currentChartData,
    currentTimestamp,
    chartType.unit,
    isFftChart ? currentMetric : 'sec',
    darkMode,
  );
  const axisName = chartType.series.find((axis) => axis.axisId === axisId)?.axisName || '';

  const filterDate = (date: Date) => {
    const dateKey: string = format(date, 'yyyy-MM-dd');

    return !!currentTimestamps?.[dateKey];
  };

  const onChangeDate = (date: Date | null) => {
    if (date) {
      const selectedNewDateTime = { date: format(date, 'yyyy-MM-dd'), time: format(date, 'HH:mm') };

      if (!currentTimestamps?.[selectedNewDateTime.date]?.[selectedNewDateTime.time]) {
        const availableTimes = Object.keys(currentTimestamps?.[selectedNewDateTime.date] || {});
        selectedNewDateTime.time = availableTimes[0];
      }

      setSelectedDateTime(selectedNewDateTime);
    }
  };

  const chartTitle = getChartPrintTitle(
    t,
    point,
    chartType,
    axisName,
    `${selectedDateTime.date} ${selectedDateTime.time}`,
  );

  if (isTimestampsFetching || isTimestampsImpactFetching) {
    return (
      <div className={style.skeletonWrapper}>
        <SkeletonBlock className={style.skeletonText} />
      </div>
    );
  }

  return (
    <div className={cx(style.chart, 'onPrintChart')}>
      <div className={cx(style.chartHead, 'onPrintHead')}>
        <div className={style.chartInfo} onClick={() => setIsOpenChart(!isOpenChart)}>
          <ChartArrowIcon
            className={cx(style.chartArrowIcon, { [style.chartArrowIconIsOpen]: isOpenChart })}
          />
          <span className={style.chartInfoTitle}>
            {t(chartType.label)} {!!chartType.unit && `(${chartType.unit})`}
          </span>
        </div>
        {!!point?.wosTrigger && (
          <div
            className={style.wosFft}
            data-tooltip-id="chart-info"
            data-tooltip-content={t(
              'Wake-on-Shake is enabled for this sensor, which will determine when vibration readings are collected',
            )}
          >
            WoS
          </div>
        )}
        <div className={isFftChart ? style.chartFftActions : style.chartTwfActions}>
          <RefetchIcon
            className={style.refetchIcon}
            data-tooltip-id="chart-info"
            data-tooltip-content={t(`Refresh chart data`)}
            onClick={() => refetch()}
          />
          <Tabs
            value={axisId}
            tabsItems={chartType.series}
            label={t('Data axis')}
            type={'woHelperText'}
            onChange={setAxisId}
            itemKeys={{ valueFieldKey: 'axisId', labelFieldKey: 'axisName' }}
          />
          {isFftChart && (
            <Tabs
              value={currentMetric}
              tabsItems={fftMetricsList}
              label={t('Frequency Unit')}
              type={'woHelperText'}
              onChange={setCurrentMetric}
            />
          )}

          <DatePicker
            selected={currentDate}
            className={style.datepicker}
            dateFormat={dateTimeFormat}
            filterDate={filterDate}
            onChange={onChangeDate}
            popperClassName={style.datepickerPopper}
            injectTimes={availableTimes}
            showMonthDropdown
            showYearDropdown
            showTimeSelect
            type={'woHelperText'}
          />
          <div
            className={style.downloadActions}
            data-tooltip-id={`download-${chartType.alias}-context-menu`}
            data-tooltip-content={JSON.stringify({
              fftChartType: currentChartType,
              readingId,
              axisId,
            })}
          >
            <ThreeDotsMoreIcon />
          </div>
        </div>
      </div>
      <div className={cx(style.chartView, 'chartViewOnPrint', { [style.chartViewOpen]: isOpenChart })}>
        {isChartFetching && <SkeletonBlock className={style.skeletonChart} />}
        {!isChartFetching && (
          <div className={cx(style.chartWrapper, 'chartWrapperOnPrint')} ref={printRef}>
            <div className={cx(style.printTitle, 'onPrintTitle')}>{chartTitle}</div>
            {!currentChartData.length ? (
              <div className={style.noData}>{t('No Data')}</div>
            ) : (
              <HighchartsReact
                key={chartType.label}
                ref={chartComponentRef}
                highcharts={Highcharts}
                options={chartOptions}
                containerProps={{ className: 'printSensorHighchartsChart' }}
              />
            )}
          </div>
        )}
      </div>
      <DownloadContextMenu
        id={`download-${chartType.alias}-context-menu`}
        dateTimeFormat={dateTimeFormat}
        point={point}
        printRef={printRef}
        chartTypeLabel={chartType.label}
        chartTitle={chartTitle}
        chartSeries={chartType.series}
      />
    </div>
  );
};

export default ChartTwfFftView;
